import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
	AfterViewChecked,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	LOCALE_ID,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	Renderer2,
	SimpleChanges,
	ViewChildren,
	ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { createSelector, select, Store } from '@ngrx/store';
import { GapMenu, GapSelect } from 'guest-app-ui';
import { isEmpty, isFunction } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { LocaleService } from 'src/app/core/locale/locale.service';
import { Details } from 'src/app/core/user/user.models';
import { selectUserDemographics } from 'src/app/core/user/user.selectors';
import { getByIndex } from 'src/app/core/user/user.utils';
import { COUNTRY_CODE } from 'src/app/feature/dynamic-form/input/dynamic-form-control.models';
import { COUNTRIES, REGIONS } from 'src/app/feature/dynamic-form/utilities/country-properties';
import { Dictionary, PrimitiveDictionary } from 'src/app/shared/types/dictionary';
import { toInt } from 'src/app/shared/utilities/number.utils';
import { validArray, validObject, validString } from 'src/app/shared/utilities/types.utils';
import { guid } from '../../shared/utilities/guid.utils';
import { DOCUMENT } from '../injection-token/document';
import { markFormAsDirty } from './dynamic-form.utils';
import { DynamicFormControl } from './input/dynamic-form-control';
import { DynamicFormEmpty } from './input/dynamic-form-empty';
import { TransformDecimalDirective } from './transform/transform-decimal.directive';
import { TransformMonthYearDirective } from './transform/transform-month-year.directive';
import { TransformNumbersOnlyDirective } from './transform/transform-numbers-only.directive';
import { CountryProperties, Region, Regions } from './utilities/country-props.models';

export interface DynamicFormRow {
	[key: string]: UntypedFormControl;
}

export interface DynamicFormChangedEvent {
	valid: boolean;
	value: Dictionary<any | undefined>;
}

export interface DynamicFormTransforms {
	decimal?: number;
	numbersOnly?: boolean;
	monthYear?: boolean;
}

export interface CrossFieldValidatorFn extends ValidatorFn {
	bilateral?: boolean;
	sourceFieldName: string;
	modifierFieldName: string;
}

export interface DynamicFormChanges<T> {
	valid?: boolean;
	value: Partial<T>;
}

export const BreakPointMap = new Map([
	[Breakpoints.XSmall, 'XSmall'],
	[Breakpoints.Small, 'Small'],
	[Breakpoints.Medium, 'Medium'],
	[Breakpoints.Large, 'Large'],
	[Breakpoints.XLarge, 'XLarge'],
]);

interface ToggleObj {
	key?: {
		name?: string;
		open?: boolean;
	};
}
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'ahp-dynamic-form',
	templateUrl: './dynamic-form.component.html',
	styleUrls: ['./dynamic-form.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class DynamicFormComponent implements OnInit, OnDestroy, OnChanges, AfterViewChecked {
	/**
	 * If you want to change the layout on XS devices.
	 */
	@Input()
	public layoutXs?: 'column' | 'row';
	/**
	 * Has the form been submitted?
	 */
	@Input()
	public formSubmitted?: boolean;
	/**
	 * Your form layout.
	 */
	@Input()
	public controls: DynamicFormControl<any>[][];
	/**
	 * If you want your rows to have an even number of columns.
	 */
	@Input()
	public evenColumns: boolean;
	/**
	 * If you want to use label the form by something
	 */
	@Input()
	public ariaDescribedBy: string;
	/**
	 * If the component parent wants to set up enhanced phone number
	 * @param value The new value for the private property _enhancedPhoneNumber
	 */
	@Input('enhancedPhoneNumber')
	public set enhancedPhoneNumber(value: EnhancedPhoneNumber) {
		if (value && validObject(value)) {
			this._enhancedPhoneNumber = value;
			this.showFlag = this._enhancedPhoneNumber.display_with_flags;
			this.useEnhancedPhoneNumber = this._enhancedPhoneNumber.use_enhanced_phone_number;
		} else {
			this._enhancedPhoneNumber = null;
		}
	}
	/**
	 * Getter method to make available outside the class the private property _enhancedPhoneNumber
	 * @return The private property _enhancedPhoneNumber
	 */
	public get enhancedPhoneNumber(): EnhancedPhoneNumber {
		return this._enhancedPhoneNumber;
	}
	/**
	 * For listening to when the form is valid or not.
	 */
	@Output()
	public formValidityChange = new EventEmitter<DynamicFormChangedEvent>();
	/**
	 * Called once the form is touched
	 */
	@Output()
	public formTouched = new EventEmitter<void>();
	/**
	 * The text inputs so we can dynamically add directives.
	 */
	@ViewChildren('textInput')
	public textInputs: ElementRef[];
	/**
	 * The ID for the form.
	 */
	public readonly formId: string = guid();
	/**
	 * The countries array.
	 */
	public countries: CountryProperties[] = COUNTRIES;
	/**
	 * Our iterator for the view.
	 */
	public controlIterator: DynamicFormRow[];
	/**
	 * Our Form group
	 */
	public formGroup: UntypedFormGroup;
	/**
	 * The regions to use in the display.
	 */
	public regions: Regions = REGIONS;
	/**
	 * The list of telephone codes.
	 */
	public telephoneCodes: string[] = [];
	/**
	 * The selected app configs.
	 */
	public appConfigs: {
		disable_zip_code_validation?: boolean;
		default_country_code?: string;
		default_phone_code?: string;
		disable_min_phone_length?: boolean;
	};
	/**
	 * It is used as the conditional used to show flags countries or not
	 */
	public showFlag = false;
	/**
	 * It is used as the conditional to show the dropdown with country codes
	 */
	public useEnhancedPhoneNumber = false;
	/**
	 * Boolean used to collapse the dropdown for country code
	 */
	public toggle = false;
	/**
	 * Object to manage the dropdown component status open/closed
	 */
	public toggleObj: ToggleObj = {};
	/**
	 * The list of phone type that  we have to implement on this dynamic form
	 */
	public phoneInputKeys = ['homePhone', 'mobilePhone', 'phone'];
	/**
	 * This post fix helps to manage the different country code for each dropdown
	 */
	public countryCodePostFix = COUNTRY_CODE;
	/**
	 *  User Demographics to be used for card update
	 */
	public userDetails: Details;
	/**
	 *  DOM element that relates to Gap MEnu
	 */
	public prefixMenu: GapMenu;
	/**
	 * The current screen size
	 */
	public currentScreenSize: string;
	/**
	 * private property used for the getter and setter methods of the input enhancedPhoneNumber
	 * @private
	 */
	private _enhancedPhoneNumber: EnhancedPhoneNumber;
	/**
	 * So we can unsubscribe when the form is destroyed.
	 * @private
	 */
	private _unsubscribeFromForm$: Subject<void>;
	/**
	 * Holds our validator functions, so we can check for CrossFieldValidatorFn's
	 * @private
	 */
	private _formValidators: (ValidatorFn | CrossFieldValidatorFn)[];
	/**
	 * Directives added.
	 * @private
	 */
	private _formDirectives: any[] = [];
	/**
	 * The last value
	 * @private
	 */
	private _lastValue: PrimitiveDictionary = {};
	/**
	 * A flag for when we need to regenerate directives because the controls that came in have changed.
	 * @private
	 */
	private _scheduleDirectives = false;
	/**
	 * Listens for when the component gets destroyed.
	 * @private
	 */
	private _unsubscribe$ = new Subject<void>();
	/**
	 * the card index
	 * @private
	 */
	private _cardIndex: number;

	/**
	 * @param _document
	 * @param _localeId Our _locale id
	 * @param breakPointObserver$ observe the breakpoints
	 * @param _locale The _locale service
	 * @param _renderer The components _renderer
	 * @param _changeDetector The components change detector reference
	 * @param _route
	 * @param _store$
	 */
	constructor(
		@Inject(DOCUMENT) private _document: Document,
		@Inject(LOCALE_ID) private _localeId: string,
		public breakPointObserver$: BreakpointObserver,
		private _locale: LocaleService,
		private _renderer: Renderer2,
		private _changeDetector: ChangeDetectorRef,
		private _route: ActivatedRoute,
		private _store$: Store<AppState>
	) {
		this._route.params.pipe(takeUntil(this._unsubscribe$)).subscribe((routeParams) => {
			this._cardIndex = toInt(routeParams.id);
		});

		this.breakPointObserver$
			.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((result) => {
				for (const query of Object.keys(result.breakpoints)) {
					if (result.breakpoints[query]) {
						this.currentScreenSize = BreakPointMap.get(query) ?? 'Unknown';
					}
				}
			});

		this._store$
			.pipe(
				select(
					createSelector(
						selectUserDemographics,
						selectAppConfig([
							'disable_zip_code_validation',
							'default_country_code',
							'default_phone_code',
							'disable_min_phone_length',
						]),
						(userDemographics, configs) => {
							return {
								userDemographics,
								configs,
							};
						}
					)
				),
				takeUntil(this._unsubscribe$)
			)
			.subscribe(({ userDemographics, configs }) => {
				this.userDetails = userDemographics.find(getByIndex(this._cardIndex));
				this.appConfigs = configs;
			});
		this._buildToggleStatus(this.phoneInputKeys);
	}

	/**
	 * Called when the component is initialized
	 */
	public ngOnInit(): void {
		this._changeDetector.detectChanges();
		this.prefixMenu = document.getElementById('prefix-menu') as GapMenu;
	}

	/**
	 * The function which is called when proprties change on the directive.
	 * @param changes The changes object.
	 */
	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.controls?.currentValue) {
			this.initializeFormGroup(changes.controls.currentValue);
		}

		if (changes.formSubmitted?.currentValue) {
			markFormAsDirty(this.formGroup);
		}
	}
	/**
	 * Called once, after the every render. We need to assign our directives here.
	 */
	public ngAfterViewChecked(): void {
		if (!this.textInputs || !this._scheduleDirectives) {
			return;
		}

		this._scheduleDirectives = false;
		this.destroyDirectives();

		this.textInputs.forEach((el: ElementRef) => {
			const control: DynamicFormControl<any> | void = this.getTransformsByName(el.nativeElement.name);

			if (control && !isEmpty(control.compiledTransforms)) {
				this.addControlDirectives(el, control);
			}
		});
	}

	/**
	 * When the component is about to be destroyed.
	 */
	public ngOnDestroy(): void {
		this.destroyDirectives();
		if (this._unsubscribeFromForm$) {
			this._unsubscribeFromForm$.next();
			this._unsubscribeFromForm$.complete();
		}
		if (this._unsubscribe$) {
			this._unsubscribe$.next();
			this._unsubscribe$.complete();
		}
	}

	/**
	 * If we have an enter pressed handler, use it, otherwise do nothing
	 * @param control
	 */
	public handleEnterPress(control: DynamicFormControl<any>): void {
		if (control.onEnterPressed) {
			control.onEnterPressed();
		}
	}

	/**
	 * If we have a space keydown handler, use it, otherwise do nothing
	 * @param control the control being used
	 * @param $event the keyboard event
	 */
	public handleKeyDownSpacePress(control: DynamicFormControl<any>, $event: KeyboardEvent): void {
		if (control.onKeyDownSpace) {
			control.onKeyDownSpace($event);
		}
	}

	/**
	 * Toggle country list drop down.
	 * @public
	 * @param enable Will be true if the user clicks to open the menu, and false if they select an item from the menu
	 * @param dropDownName Will be the name of control key.
	 */
	public toggleDropdown(enable: boolean, dropDownName): void {
		this.toggleObj[dropDownName].open = enable && !this.toggleObj[dropDownName].open;
	}

	/**
	 * Method called once one item is selected from dropdown
	 * @public
	 * @param controlKey It contains the list of country code values
	 * @param country It contains the country object
	 */
	public selectedCountry(controlKey: string, country: CountryProperties): void {
		this.formGroup.controls[`${controlKey}${this.countryCodePostFix}`].setValue(country);
	}

	/**
	 * Get the change detector reference
	 */
	public getChangeDetectorRef(): ChangeDetectorRef {
		return this._changeDetector;
	}

	/**
	 * Adds directives to a control
	 * @param el The element to add the transforms directives to
	 * @param control The control reference
	 */
	public addControlDirectives(el: ElementRef, control: DynamicFormControl<any>): void {
		const { _renderer } = this;
		const { nativeElement } = el;
		const { compiledTransforms } = control;

		Object.keys(compiledTransforms).forEach((key: string) => {
			switch (key) {
				case 'decimal':
					_renderer.setAttribute(nativeElement, 'apTransformDecimal', '');
					const decimalDirective = new TransformDecimalDirective(el, this._renderer);
					if (compiledTransforms[key]) {
						decimalDirective.appTransformDecimal = compiledTransforms[key];
					}
					this._formDirectives.push(decimalDirective);
					break;
				case 'numbersOnly':
					_renderer.setAttribute(nativeElement, 'apTransformNumbersOnly', '');
					const numbersOnlyDirective = new TransformNumbersOnlyDirective(el, this._renderer);
					numbersOnlyDirective.appTransformNumbersOnly = compiledTransforms.hasOwnProperty('decimal');
					this._formDirectives.push(numbersOnlyDirective);
					break;
				case 'monthYear':
					_renderer.setAttribute(nativeElement, 'apTransformMonthYear', '');
					this._formDirectives.push(new TransformMonthYearDirective(el, this._renderer));
					break;
			}
		});
	}

	/**
	 * Returns the transforms to add based on the key of a control
	 * @param name The key to find by
	 */
	public getTransformsByName(name?: string): DynamicFormControl<any> | void {
		if (!validString(name) || !validArray(this.controls)) {
			return;
		}

		for (let i = this.controls.length - 1; i > -1; i--) {
			const row = this.controls[i];
			for (let n = row.length - 1; n > -1; n--) {
				if (row[n].key === name) {
					return row[n];
				}
			}
		}
	}

	/**
	 * Initializes a form group based on the controls passed in
	 * @param controls The controls to initialize with the form group
	 */
	public initializeFormGroup(controls: DynamicFormControl<any>[][]): void {
		if (!Array.isArray(controls)) {
			controls = [];
		}

		if (this.evenColumns) {
			this.makeEvenColumns(this.controls);
		}

		// unsubscribe from our previous
		if (this._unsubscribeFromForm$) {
			this.destroyDirectives();
			this._unsubscribeFromForm$.next();
			this._unsubscribeFromForm$.complete();
		}

		this._unsubscribeFromForm$ = new Subject();
		this.buildControls(this.controls);
		this.buildFormGroup(this.controlIterator, this.controls);
		this.buildCountryCodeForPhone(this.controls);
		this.assignCrossFieldValidationChanges(this.formGroup);

		// Must come before we register the valueChanges event below.
		if (this.hasStateControl(this.controls)) {
			this.assignStateAndCountryChange(this.formGroup);
		}

		this.formGroup.valueChanges.pipe(takeUntil(this._unsubscribeFromForm$)).subscribe((changes) => {
			this.formValidityChange.emit({
				valid: this.formGroup.valid,
				value: this.formGroup.value,
			});

			this._lastValue = { ...this.formGroup.value };
		});

		Object.keys(this.formGroup.controls).forEach((key) => {
			const control = this.formGroup.controls[key];
			if (control.value) {
				control.markAsDirty();
				control.markAsTouched();
			}
			control.updateValueAndValidity();
		});

		this.formValidityChange.emit({
			valid: this.formGroup.valid,
			value: this.formGroup.value,
		});

		// Schedule directives to be destroy and reattached.
		this._scheduleDirectives = true;
	}

	/**
	 * Assigns a subscription to the valueChanges observable to be able to modify the other inputs required.
	 * @param formGroup The form group to watch
	 */
	public assignStateAndCountryChange(formGroup: UntypedFormGroup): void {
		const stateKey = this.getStateControlKey(this.controls);
		const countryKey = this.getCountryControlKey(this.controls);
		let lastCountry = formGroup.controls[countryKey].value;

		if (!validString(stateKey) || !validString(countryKey) || !formGroup.controls[countryKey]) {
			return;
		}

		formGroup.controls[countryKey].valueChanges
			.pipe(takeUntil(this._unsubscribeFromForm$))
			.subscribe((newCountry) => {
				let validators: ValidatorFn[] = null;
				const control = formGroup.controls[stateKey];

				if (this.isCountryWithRegion(newCountry)) {
					validators = [Validators.required];
				}

				control.setValidators(validators);

				if (lastCountry !== newCountry) {
					control.setValue('');
					lastCountry = newCountry;
				}

				this._scheduleStateLayout(stateKey);
			});

		const { default_country_code } = this.appConfigs;
		if (!validString(formGroup.controls[countryKey].value) && validString(default_country_code)) {
			formGroup.controls[countryKey].setValue(default_country_code);
		}
	}

	/**
	 * Find out if a specific country has regions configured.
	 * @param country The country to check
	 */
	public isCountryWithRegion(country: string): boolean {
		return ['AU', 'CA', 'MX', 'US'].includes(country);
	}

	/**
	 * Returns whether we display state or province based on the country.
	 * @param country The country code
	 * @param defaultLabel The default label
	 */
	public getStateProvinceLabel(country: string, defaultLabel: string): string {
		if (validString(country)) {
			switch (country) {
				case 'MX':
				case 'US':
				case 'AU':
					return 'common.state';
				default:
					return 'common.province';
			}
		}

		return defaultLabel;
	}

	/**
	 * It allows to return the image associated to the country received on their argument
	 * @public
	 * @param controlKey The country code value selected
	 * @returns It contains the path for the image flag selected
	 */
	public getFlagChosen(controlKey: string): string {
		return (
			'assets/image/flags/' +
			this.formGroup.controls[`${controlKey}${this.countryCodePostFix}`]?.value?.code.toLowerCase() +
			'.png'
		);
	}

	public getFlagCodeChosen(controlKey: string): string {
		return this.formGroup.controls[`${controlKey}${this.countryCodePostFix}`]?.value?.code.toLowerCase();
	}

	/**
	 * It selects the country code for the specific dropdown selected
	 * @param controlKey
	 * @returns the country code selected
	 */
	public getCountryCodeChosen(controlKey: string): string {
		return this.formGroup.controls[`${controlKey}${this.countryCodePostFix}`]?.value?.countryCode;
	}
	/**
	 * Returns Region label based on _locale if available || default label
	 * @param region Region from Dropdown list
	 * @returns Region Label
	 */
	public getRegionLabel(region: Region): string {
		const localeParts = this._localeId.toString().toLowerCase().split('-');

		return region[localeParts[0]] || region.label;
	}

	/**
	 * Finds the first control based on a type
	 * @param controls The controls to check through
	 * @param type The type to check against
	 */
	public getControlType(controls: DynamicFormControl<any>[][], type: string): DynamicFormControl<string> {
		for (const row of controls) {
			for (const control of row) {
				if (control.controlType === type) {
					return control;
				}
			}
		}
	}

	/**
	 * Find out if we have a state control
	 * @param controls The controls to check through
	 */
	public hasStateControl(controls: DynamicFormControl<any>[][]): boolean {
		return !!this.getControlType(controls, 'state');
	}

	/**
	 * Returns the key for the first control it finds that has a control type of state
	 * @param controls The controls to check
	 */
	public getStateControlKey(controls: DynamicFormControl<any>[][]): string {
		const control = this.getControlType(controls, 'state');
		if (control) {
			return control.key;
		}
	}

	/**
	 * Returns the key for the country control it finds that has a control type of state
	 * @param controls The controls to check
	 */
	public getCountryControlKey(controls: DynamicFormControl<any>[][]): string {
		const control = this.getControlType(controls, 'state');
		if (control) {
			return control.countryKey;
		}
	}
	/**
	 * Assigns all cross-field validation changes. This is where one field depends on another and vice-versa.
	 * @param formGroup The form group.
	 */
	public assignCrossFieldValidationChanges(formGroup: UntypedFormGroup): void {
		this._formValidators.forEach((validator: CrossFieldValidatorFn) => {
			// If a cross field validator fn
			if (!validator.sourceFieldName) {
				return;
			}

			const sourceControl = formGroup.controls[validator.sourceFieldName];
			const modifierControl = formGroup.controls[validator.modifierFieldName];

			this.subscribeCrossFieldValidation(sourceControl, modifierControl);
			if (validator.bilateral) {
				this.subscribeCrossFieldValidation(modifierControl, sourceControl);
			}
		});
	}

	/**
	 * Subscribes to cross-field validation changes to mark eachother as dirty and touched.
	 * @param sourceControl The source control
	 * @param modifierControl The modifier control
	 */
	public subscribeCrossFieldValidation(sourceControl: AbstractControl, modifierControl: AbstractControl): void {
		modifierControl.valueChanges.pipe(takeUntil(this._unsubscribeFromForm$)).subscribe(() => {
			if (sourceControl.dirty || sourceControl.value) {
				sourceControl.markAsDirty();
				sourceControl.markAsTouched();
				sourceControl.updateValueAndValidity();
			}
		});
	}

	/**
	 * Resets the form.
	 */
	public reset(): void {
		this.formGroup.reset();
	}

	/**
	 * Forces all rows to have an even number of columns by injecting empty controls.
	 * @param controls The layout to modify to have an even number of rows
	 */
	public makeEvenColumns(controls: DynamicFormControl<any>[][]): void {
		const maxColumns = controls.reduce((lastMax: number, row: DynamicFormControl<any>[]): number => {
			return Math.max(row.length, lastMax);
		}, 0);

		controls.forEach((row: DynamicFormControl<any>[]) => {
			if (row.length < maxColumns) {
				row.fill(new DynamicFormEmpty(), row.length - 1, maxColumns - 1);
			}
		});
	}

	/**
	 * Destroys all bound directives.
	 */
	public destroyDirectives(): void {
		this._formDirectives.forEach((directive) => {
			if (isFunction(directive.ngOnDestroy)) {
				directive.ngOnDestroy();
			}
		});
		this._formDirectives.length = 0;
	}

	/**
	 * Builds our DynamicFormControls into actual workable FormControl objects
	 * @param inputControls The DynamicFormControl objects you wish to build into actual FormControls
	 */
	public buildControls(inputControls: DynamicFormControl<any>[][] = []): void {
		this.controlIterator = inputControls.reduce((acc: DynamicFormRow[], row: DynamicFormControl<any>[]) => {
			if (!row.length) {
				return acc;
			}

			const formControlRow = row.reduce(
				(innerAcc: DynamicFormRow, control: DynamicFormControl<any>): DynamicFormRow => {
					innerAcc[control.key] = new UntypedFormControl(
						control.value,
						control.compiledValidators.filter((validator, idx) => {
							const validatorsList = control.validators.split(',');

							if ((validator as any).isZipCodeValidator) {
								return !this.appConfigs.disable_zip_code_validation;
							} else if (
								this.phoneInputKeys.includes(control.key) &&
								validatorsList.findIndex((value) => /^minLength/.test(value)) === idx
							) {
								return !this.appConfigs.disable_min_phone_length;
							}

							return true;
						}),
						control.asyncValidators ?? []
					);
					return innerAcc;
				},
				{}
			);

			acc.push(formControlRow);

			return acc;
		}, []);
	}

	/**
	 * It builds the control for country code object based their type
	 * it can be homePhoneCountryCode, mobilePhoneCountryCode and phoneCountryCode
	 * @param inputControls The list of controls to be checked to made each country code object
	 */
	public buildCountryCodeForPhone(inputControls: DynamicFormControl<any>[][] = []): void {
		const phoneControls = this._getAllPhoneInputsControl(inputControls);

		phoneControls.forEach((control, index) => {
			let country;
			if (this.userDetails) {
				country = this._getCountry(
					this.userDetails.phone?.[index]?.region_code || this.appConfigs.default_country_code || 'US'
				);
			} else {
				country = this._getCountry(this.appConfigs.default_country_code || 'US');
			}
			this.formGroup.addControl(`${control.key}${this.countryCodePostFix}`, new UntypedFormControl(country));
		});
	}

	/**
	 * Turns our dynamic form rows into a form group
	 * @param rows The actual controls used in the template
	 * @param controls The initial pre-compiled controls
	 */
	public buildFormGroup(rows: DynamicFormRow[], controls: DynamicFormControl<any>[][] = []): void {
		const groups = rows.reduce((acc: { [key: string]: UntypedFormControl }, row: DynamicFormRow) => {
			Object.keys(row).forEach((controlKey: string) => {
				acc[controlKey] = row[controlKey];
			});

			return acc;
		}, {});

		this._formValidators = controls.reduce((acc: CrossFieldValidatorFn[], row: DynamicFormControl<any>[]) => {
			if (!row.length) {
				return acc;
			}
			return row.reduce(
				(
					innerAcc: ValidatorFn[],
					control: DynamicFormControl<any>
				): (CrossFieldValidatorFn | ValidatorFn)[] => {
					return innerAcc.concat(
						control.compiledFormValidators.filter((validatorFn) => {
							return (
								!this.appConfigs.disable_zip_code_validation || !(validatorFn as any).isZipCodeValidator
							);
						})
					);
				},
				acc
			);
		}, []);

		this.formGroup = new UntypedFormGroup(groups, this._formValidators);
	}

	/**
	 * Find out if we should display the max length as a hint.
	 * @param control The control to check
	 */
	public displayMaxLength(control: DynamicFormControl<any>): boolean {
		return control.maxLengthHint && control.maxLength && ['text', 'textarea'].includes(control.controlType);
	}

	/**
	 * Find out if we need to show errors for a specific control
	 * @param control The form group's control to check
	 */
	public displayError(control: AbstractControl): boolean {
		return !!(control.errors && (this.formSubmitted || control.touched));
	}

	/**
	 * Returns the errors to display under the input.
	 * @param errors The errors to display
	 */
	public getErrors(errors: Dictionary<any>): string | SafeHtml {
		const SECTION = 'error';
		const { _locale } = this;

		if (!errors) {
			return null;
		}

		if (errors.required) {
			return _locale.get(`${SECTION}.fieldrequired`);
		}

		if (errors.email) {
			return _locale.get(`${SECTION}.email`);
		}

		if (errors.emailMatch) {
			return _locale.get(`${SECTION}.emailmatch`);
		}

		if (errors.min) {
			const min = _locale.get(`${SECTION}.minimum`);
			return min.replace('@@min', errors.min.min).replace('@@actual', errors.min.actual);
		}

		if (errors.max) {
			const max = _locale.get(`${SECTION}.maximum`);
			return max.replace('@@max', errors.max.max).replace('@@actual', errors.max.actual);
		}

		if (errors.minlength) {
			const len = _locale.get(`${SECTION}.minlength`);
			return len
				.replace('@@len', errors.minlength.requiredLength)
				.replace('@@actual', errors.minlength.actualLength);
		}

		if (errors.maxlength) {
			const len = _locale.get(`${SECTION}.maxlength`);
			return len
				.replace('@@len', errors.maxlength.requiredLength)
				.replace('@@actual', errors.maxlength.actualLength);
		}

		if (errors.numericOnly) {
			return _locale.get(
				`${SECTION}.numeric${
					errors.numericOnly.positiveOnly && errors.numericOnly.isNegative ? 'positive' : ''
				}`
			);
		}

		if (errors.zipCode) {
			if (errors.match) {
				const foundCountry = this.countries.find((country) => {
					return country[0] === errors.match;
				});
				const str = _locale.get(`${SECTION}.zipcode_match`);

				if (foundCountry) {
					return str.replace(/@@match/gi, foundCountry[1]);
				}
				return str;
			}
			return _locale.get(`${SECTION}.zipcode`);
		}

		const keys = Object.keys(errors);

		if (keys.length) {
			return _locale.get(`${SECTION}.${keys[0]}`);
		}

		return '';
	}

	/**
	 * It returns the country based on code received as argument
	 * @private
	 * @param code It contains the country code
	 * @returns the country object selected
	 */
	private _getCountry(code: string): CountryProperties {
		return this.countries.find((country) => country.code === code);
	}

	/**
	 * It populates the toggle object where will be managed the status of dropdown component
	 * @param componentsNameList The names of dropdown components
	 */
	private _buildToggleStatus(componentsNameList: string[]): void {
		componentsNameList.forEach((key) => {
			if (!this.toggleObj[key]) {
				this.toggleObj[key] = {};
				this.toggleObj[key] = {
					name: key,
					open: false,
				};
			}
		});
	}

	/**
	 * It selects all the phone controls
	 * @param controls The list of control to be integrated
	 * @returns The list of phone controls
	 */
	private _getAllPhoneInputsControl(controls: DynamicFormControl<any>[][]): DynamicFormControl<any>[] {
		const controlList: DynamicFormControl<any>[] = [];
		for (const row of controls) {
			for (const control of row) {
				if (control.controlType === 'phone') {
					controlList.push(control);
				}
			}
		}
		return controlList;
	}

	/**
	 * Returns the _document object
	 */
	private _getDocument(): Document {
		return this._document;
	}

	/**
	 * Schedules a country gap-select relayout
	 */
	private _scheduleStateLayout(gapSelectName: string): void {
		setTimeout(() => {
			const doc = this._getDocument();
			const gapSelect: GapSelect = doc.querySelector(`gap-select[name="${gapSelectName}"]`);

			if (gapSelect) {
				gapSelect.updateMenuOptions();
			}
		}, 500);
	}
}
